import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, X, UserCog, MapPin, Phone, Mail, Clock,
  CheckCircle, AlertCircle, Briefcase, Languages, Star
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const statusLabels = {
  available: { label: 'Disponible', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  assigned: { label: 'Assigné', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  on_leave: { label: 'En congé', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  inactive: { label: 'Inactif', color: 'bg-slate-100 text-slate-500', dot: 'bg-slate-400' }
};

const skillLabels = {
  prospection: 'Prospection',
  customer_service: 'Service client',
  administrative: 'Administratif',
  accounting: 'Comptabilité',
  social_media: 'Réseaux sociaux',
  content_writing: 'Rédaction',
  data_entry: 'Saisie de données',
  other: 'Autre'
};

export default function AdminAssistantsPage() {
  const [assistants, setAssistants] = useState([]);
  const [stats, setStats] = useState({ total: 0, available: 0, assigned: 0, on_leave: 0 });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingAssistant, setEditingAssistant] = useState(null);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: 'Madagascar',
    skills: [],
    languages: ['Français'],
    hourly_rate: 0,
    notes: ''
  });

  const [assignData, setAssignData] = useState({
    client_id: '',
    hours_per_week: 20,
    notes: ''
  });

  useEffect(() => {
    fetchAssistants();
    fetchStats();
    fetchClients();
  }, [filterStatus]);

  const fetchAssistants = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      let url = `${API_URL}/api/admin-crm/assistants`;
      if (filterStatus) url += `?status=${filterStatus}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAssistants(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/assistants/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchClients = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setClients(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Le nom est requis');
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      let response;
      if (editingAssistant) {
        const params = new URLSearchParams();
        Object.entries(formData).forEach(([key, value]) => {
          if (value !== '' && value !== null) {
            if (Array.isArray(value)) {
              params.append(key, value.join(','));
            } else {
              params.append(key, value.toString());
            }
          }
        });
        response = await fetch(`${API_URL}/api/admin-crm/assistants/${editingAssistant.id}?${params.toString()}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        response = await fetch(`${API_URL}/api/admin-crm/assistants`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      }

      if (response.ok) {
        toast.success(editingAssistant ? 'Assistant mis à jour' : 'Assistant créé');
        setShowModal(false);
        resetForm();
        fetchAssistants();
        fetchStats();
      } else {
        toast.error('Erreur');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!assignData.client_id || !selectedAssistant) {
      toast.error('Sélectionnez un client');
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      const params = new URLSearchParams({
        client_id: assignData.client_id,
        hours_per_week: assignData.hours_per_week.toString()
      });
      if (assignData.notes) params.append('notes', assignData.notes);

      const response = await fetch(`${API_URL}/api/admin-crm/assistants/${selectedAssistant.id}/assign?${params.toString()}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Assistant assigné');
        setShowAssignModal(false);
        setSelectedAssistant(null);
        setAssignData({ client_id: '', hours_per_week: 20, notes: '' });
        fetchAssistants();
        fetchStats();
      } else {
        toast.error('Erreur');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleUnassign = async (assistantId) => {
    if (!confirm('Désassigner cet assistant ?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/assistants/${assistantId}/unassign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Assistant désassigné');
        fetchAssistants();
        fetchStats();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (assistantId) => {
    if (!confirm('Supprimer cet assistant ?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/assistants/${assistantId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Assistant supprimé');
        fetchAssistants();
        fetchStats();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openEditModal = (assistant) => {
    setEditingAssistant(assistant);
    setFormData({
      name: assistant.name || '',
      email: assistant.email || '',
      phone: assistant.phone || '',
      location: assistant.location || 'Madagascar',
      skills: assistant.skills || [],
      languages: assistant.languages || ['Français'],
      hourly_rate: assistant.hourly_rate || 0,
      notes: assistant.notes || ''
    });
    setShowModal(true);
  };

  const openAssignModal = (assistant) => {
    setSelectedAssistant(assistant);
    setShowAssignModal(true);
  };

  const resetForm = () => {
    setEditingAssistant(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      location: 'Madagascar',
      skills: [],
      languages: ['Français'],
      hourly_rate: 0,
      notes: ''
    });
  };

  const toggleSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const filteredAssistants = assistants.filter(a => {
    const matchesSearch = a.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         a.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch && a.status !== 'inactive';
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-assistants-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Assistants</h1>
          <p className="text-rm-text-secondary">Gère tes assistants Madagascar</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-rm-primary" data-testid="new-assistant-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvel assistant
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
              <UserCog className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-rm-text">{stats.total}</p>
              <p className="text-xs text-rm-text-secondary">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.available}</p>
              <p className="text-xs text-rm-text-secondary">Disponibles</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{stats.assigned}</p>
              <p className="text-xs text-rm-text-secondary">Assignés</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">{stats.on_leave}</p>
              <p className="text-xs text-rm-text-secondary">En congé</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-white border border-slate-200 rounded-lg"
        >
          <option value="">Tous les statuts</option>
          <option value="available">Disponible</option>
          <option value="assigned">Assigné</option>
          <option value="on_leave">En congé</option>
        </select>
      </div>

      {/* Assistants Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredAssistants.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-xl border border-slate-100">
            <UserCog className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-rm-text-secondary">Aucun assistant</p>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="mt-4 bg-rm-primary">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un assistant
            </Button>
          </div>
        ) : (
          filteredAssistants.map((assistant) => (
            <div 
              key={assistant.id} 
              className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden"
              data-testid={`assistant-card-${assistant.id}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-rm-primary/10 flex items-center justify-center text-rm-primary font-semibold text-lg">
                      {assistant.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold text-rm-text">{assistant.name}</h3>
                      <div className="flex items-center gap-1 text-xs text-rm-text-secondary">
                        <MapPin className="w-3 h-3" />
                        {assistant.location}
                      </div>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusLabels[assistant.status]?.color}`}>
                    {statusLabels[assistant.status]?.label}
                  </span>
                </div>

                {/* Contact */}
                <div className="space-y-1 text-sm text-rm-text-secondary mb-3">
                  {assistant.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3" />
                      {assistant.email}
                    </div>
                  )}
                  {assistant.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-3 h-3" />
                      {assistant.phone}
                    </div>
                  )}
                </div>

                {/* Skills */}
                {assistant.skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {assistant.skills.slice(0, 3).map((skill) => (
                      <span key={skill} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                        {skillLabels[skill] || skill}
                      </span>
                    ))}
                    {assistant.skills.length > 3 && (
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                        +{assistant.skills.length - 3}
                      </span>
                    )}
                  </div>
                )}

                {/* Current Assignment */}
                {assistant.status === 'assigned' && assistant.current_assignment && (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-2 mb-3">
                    <div className="flex items-center gap-2 text-sm text-blue-700">
                      <Briefcase className="w-4 h-4" />
                      <span className="font-medium">{assistant.current_assignment.client_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-blue-600 mt-1">
                      <Clock className="w-3 h-3" />
                      {assistant.current_assignment.hours_per_week}h/semaine
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-3 border-t border-slate-100">
                  <Button size="sm" variant="outline" onClick={() => openEditModal(assistant)} className="flex-1">
                    Modifier
                  </Button>
                  {assistant.status === 'available' && (
                    <Button size="sm" onClick={() => openAssignModal(assistant)} className="flex-1 bg-rm-primary">
                      Assigner
                    </Button>
                  )}
                  {assistant.status === 'assigned' && (
                    <Button size="sm" variant="outline" onClick={() => handleUnassign(assistant.id)} className="flex-1 text-orange-600 border-orange-200 hover:bg-orange-50">
                      Désassigner
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-rm-text">
                    {editingAssistant ? 'Modifier l\'assistant' : 'Nouvel assistant'}
                  </h2>
                  <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Nom *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    placeholder="Nom complet"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Localisation</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    placeholder="Madagascar"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-2">Compétences</label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(skillLabels).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => toggleSkill(key)}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          formData.skills.includes(key)
                            ? 'bg-rm-primary text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Coût horaire (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
                  <Button type="submit" className="bg-rm-primary">
                    {editingAssistant ? 'Mettre à jour' : 'Créer'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Assign Modal */}
      <AnimatePresence>
        {showAssignModal && selectedAssistant && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowAssignModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-rm-text">Assigner {selectedAssistant.name}</h2>
                  <button onClick={() => setShowAssignModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleAssign} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Client *</label>
                  <select
                    required
                    value={assignData.client_id}
                    onChange={(e) => setAssignData({ ...assignData, client_id: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  >
                    <option value="">-- Sélectionner un client --</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name || c.company_name} - {c.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Heures par semaine</label>
                  <input
                    type="number"
                    min="1"
                    max="40"
                    value={assignData.hours_per_week}
                    onChange={(e) => setAssignData({ ...assignData, hours_per_week: parseInt(e.target.value) || 20 })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Notes</label>
                  <textarea
                    value={assignData.notes}
                    onChange={(e) => setAssignData({ ...assignData, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    rows={2}
                    placeholder="Instructions particulières..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAssignModal(false)}>Annuler</Button>
                  <Button type="submit" className="bg-rm-primary">Assigner</Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
